import { type ClassValue, clsx } from 'clsx'
import Color from 'color'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'dayjs/locale/vi'
import 'dayjs/locale/en'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { twMerge } from 'tailwind-merge'

import { BILLING_MODES } from '@/constants/subscription'
import { UserRole } from '@/constants/user'
import { defaultLocale, locales } from '@/i18n/config'
import { WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM } from '@/lib/notification'
import { AvailableLocale } from '@/types/global'
import { WebPushNotificationSubscriptionPlatform } from '@/types/notification'

import { USER_AGENT_DEVICE_TYPE } from './constants'

dayjs.extend(localizedFormat)
dayjs.extend(timezone)
dayjs.extend(utc)

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

interface GenerateEmbedCodeProps {
  fileBaseUrl: string
  chatbotId: string
}
export function generateEmbedCode({ fileBaseUrl, chatbotId }: GenerateEmbedCodeProps) {
  return `<script id="kotae-embed-js" type="text/javascript" src="${fileBaseUrl}/embed/index.min.js" data-cid="${chatbotId}" defer></script>
<link id="kotae-embed-css" rel="stylesheet" href="${fileBaseUrl}/embed/index.min.css"/>`
}

export function generateChatbotLink({ fileBaseUrl, chatbotId }: GenerateEmbedCodeProps) {
  return `${fileBaseUrl}/chat/${chatbotId}`
}

export function isLightColor(color: string) {
  let hexColor
  try {
    hexColor = Color(color).hex()
  } catch {
    hexColor = color
  }
  // Remove the '#' character if present
  const cleanedHex = hexColor.replace('#', '')

  // Convert hex to RGB values
  const r = parseInt(cleanedHex.slice(0, 2), 16)
  const g = parseInt(cleanedHex.slice(2, 4), 16)
  const b = parseInt(cleanedHex.slice(4, 6), 16)

  // Calculate perceived brightness using the luminosity formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  // Determine if the color is light based on brightness threshold
  return brightness > 155
}

export const isHexColor = (hexColor: string) => {
  const regex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/
  return regex.test(hexColor)
}

export const isColor = (color: string): boolean => {
  try {
    Color(color).hex()
    return true
  } catch {
    return false
  }
}

const isValidLocale = (locale: string) => locales.includes(locale)

export const getPreviousWeekRange = () => {
  const today = new Date()

  const formatDate = (date: Date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${year}-${month}-${day}`
  }

  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(today.getDate() - 6)

  const startDate = formatDate(sevenDaysAgo)
  const endDate = formatDate(today)

  return { startDate, endDate }
}

export const getLanguageUser = (locale?: AvailableLocale) => {
  if (locale) {
    return locale
  }
  const browserLanguage = navigator.language
  if (isValidLocale(browserLanguage)) {
    return browserLanguage
  }

  return defaultLocale
}

// Matches a leading `/<locale>` segment (`/ja` in `/ja/tutorials/embed`) without matching
// paths that merely start with those characters, such as `/january-sale`.
const LOCALE_PATH_PREFIX_REGEX = new RegExp(`^/(${locales.join('|')})(?=/|$)`)

/**
 * Rewrites an absolute URL to its localized variant for external sites that follow next-intl's
 * `as-needed` prefix strategy: `defaultLocale` is served at the site root, every other locale under
 * a `/<locale>` prefix. Assumes the target site shares this app's locale configuration.
 *
 * Any locale prefix already present on the input is replaced, so the result is the same whether the
 * caller passes a bare or an already-prefixed URL.
 *
 * External sites only (kotae.ai and friends). This app runs `localePrefix: 'never'`, so its own
 * routes never carry a locale segment.
 */
export function getLocalizedExternalUrl(url: string, locale: string): string {
  if (!url) return url

  try {
    const localizedUrl = new URL(url)
    // Strip any existing locale segment so the prefix is derived solely from `locale`.
    const basePath = localizedUrl.pathname.replace(LOCALE_PATH_PREFIX_REGEX, '') || '/'

    localizedUrl.pathname = locale === defaultLocale ? basePath : `/${locale}${basePath}`

    return localizedUrl.toString()
  } catch {
    // Not a parseable absolute URL: return it untouched rather than emitting a broken link.
    return url
  }
}

export const formatDate = (
  dateTime: Date | string,
  format: string,
  locale: AvailableLocale = 'en',
  timeZone?: string | null,
) => {
  if (!dateTime) return ''
  return dayjs
    .utc(dateTime)
    .tz(timeZone || getDeviceTimezone())
    .locale(locale)
    .format(format)
}

export const getDeviceTimezone = (): string => {
  const timezoneAliasMap: Record<string, string> = {
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  }
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return timezoneAliasMap[timezone] || timezone || 'UTC'
}

export const getPresetName = (from: Date, to: Date): string => {
  const now = new Date()
  now.setHours(23, 59, 59, 999)
  const isSameDay = (date1: Date, date2: Date) => date1.toISOString() === date2.toISOString()
  if (from.getTime() === -25200000 && isSameDay(to, now)) {
    return 'allTime'
  }
  const diffDays = Math.ceil((now.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
  switch (diffDays) {
    case 7:
      return 'last7'
    case 14:
      return 'last14'
    case 30:
      return 'last30'
    default:
      break
  }
  const fromStartOfMonth = new Date(from)
  fromStartOfMonth.setDate(1)
  fromStartOfMonth.setHours(0, 0, 0, 0)

  const sixMonthsAgo = new Date(now)
  sixMonthsAgo.setMonth(now.getMonth() - 6)
  sixMonthsAgo.setDate(1)
  sixMonthsAgo.setHours(0, 0, 0, 0)

  const twelveMonthsAgo = new Date(now)
  twelveMonthsAgo.setMonth(now.getMonth() - 12)
  twelveMonthsAgo.setDate(1)
  twelveMonthsAgo.setHours(0, 0, 0, 0)

  if (isSameDay(fromStartOfMonth, sixMonthsAgo)) {
    return 'last6Months'
  }
  if (isSameDay(fromStartOfMonth, twelveMonthsAgo)) {
    return 'last12Months'
  }
  return 'custom'
}

export const formatTimestampHistory = (
  date: string | Date | dayjs.Dayjs,
  locale: string = 'en',
  timezone: string = '',
): string => {
  dayjs.locale(locale)

  timezone = timezone || getDeviceTimezone() // ensures to fallback to device timezone if timezone is falsy

  const now = dayjs().tz(timezone).startOf('day')
  const inputDate = dayjs(date).tz(timezone)

  const isToday = inputDate.isSame(now, 'day')
  const isYesterday = inputDate.isSame(now.subtract(1, 'day'), 'day')
  const isWithinAWeek = inputDate.isAfter(now.subtract(7, 'day'))

  if (locale === 'ja') {
    const weekDays = ['日', '月', '火', '水', '木', '金', '土']
    const weekday = weekDays[inputDate.day()]

    if (isToday) {
      const hour = inputDate.hour()
      const minute = inputDate.minute()
      const ampm = hour < 12 ? '午前' : '午後'
      const hour12 = hour % 12 === 0 ? 12 : hour % 12
      return `${ampm}${hour12}時${minute.toString().padStart(2, '0')}分`
    }

    if (isYesterday) {
      return '昨日'
    }

    if (isWithinAWeek) {
      return `${inputDate.month() + 1}月${inputDate.date()}日（${weekday}）`
    }

    return `${inputDate.year()}年${inputDate.month() + 1}月${inputDate.date()}日`
  }

  // Non-Japanese locales
  if (isToday) {
    return inputDate.format('hh:mm a')
  }

  if (isYesterday) {
    return locale === 'vi' ? 'Hôm qua' : 'Yesterday'
  }

  if (isWithinAWeek) {
    return inputDate.format('ddd, D MMM')
  }

  return inputDate.format('D MMM YYYY')
}

export function getAllPricingPlanPriceIds() {
  const monthlyIds = {
    monthlyStarter: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_STARTER!),
    monthlyPro: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_PRO!),
    monthlyVoice: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_VOICE!),
  }
  const yearlyIds = {
    yearlyStarter: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_STARTER_YEARLY!),
    yearlyPro: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_PRO_YEARLY!),
    yearlyVoice: getPriceIds(process.env.NEXT_PUBLIC_PADDLE_PRICING_PLAN_VOICE_YEARLY!),
  }

  const monthly = {
    monthlyStarter: monthlyIds?.monthlyStarter?.at(-1) as string,
    monthlyPro: monthlyIds?.monthlyPro?.at(-1) as string,
    monthlyVoice: monthlyIds?.monthlyVoice?.at(-1) as string,
  }
  const yearly = {
    yearlyStarter: yearlyIds?.yearlyStarter?.at(-1) as string,
    yearlyPro: yearlyIds?.yearlyPro?.at(-1) as string,
    yearlyVoice: yearlyIds?.yearlyVoice?.at(-1) as string,
  }

  return {
    monthlyIds,
    yearlyIds,
    monthly,
    yearly,
    all: {
      ...monthly,
      ...yearly,
    },
    yearlyToMonthlyPlanPriceIdMap: {
      [yearly.yearlyStarter]: monthly.monthlyStarter,
      [yearly.yearlyPro]: monthly.monthlyPro,
      [yearly.yearlyVoice]: monthly.monthlyVoice,
    },
  }
}

function getPriceIds(value: string): string[] {
  const ids = (value || '')
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean)
  return ids
}

export enum PlanTier {
  STARTER = 1,
  PRO = 2,
  PRO_PLUS_VOICE = 3,
}
export interface PricingPlan {
  priceId: string
  name: string
  features: string[]
  isPopular: boolean
  isCurrent?: boolean
  billingMode: string
  tier: PlanTier
}

export function getAllPricingPlanDisplay({
  currentPriceId,
  t,
}: {
  currentPriceId?: string
  t: (_key: string) => string
}) {
  const planPriceIds = getAllPricingPlanPriceIds()
  const hasCurrentPriceId = currentPriceId !== null && currentPriceId !== undefined

  const monthlyPlans: PricingPlan[] = [
    {
      priceId: planPriceIds.monthly.monthlyStarter,
      name: t('plans.starter.title'),
      features: t('plans.starter.features').split(';'),
      isPopular: false,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.monthlyIds?.monthlyStarter?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.MONTHLY,
      tier: PlanTier.STARTER,
    },
    {
      priceId: planPriceIds.monthly.monthlyPro,
      name: t('plans.pro.title'),
      features: t('plans.pro.features').split(';'),
      isPopular: true,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.monthlyIds?.monthlyPro?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.MONTHLY,
      tier: PlanTier.PRO,
    },
    {
      priceId: planPriceIds.monthly.monthlyVoice,
      name: t('plans.proPlusVoice.title'),
      features: t('plans.proPlusVoice.features').split(';'),
      isPopular: false,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.monthlyIds?.monthlyVoice?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.MONTHLY,
      tier: PlanTier.PRO_PLUS_VOICE,
    },
  ]

  const yearlyPlans: PricingPlan[] = [
    {
      priceId: planPriceIds.yearly.yearlyStarter,
      name: t('plans.starter.title'),
      features: t('plans.starter.features').split(';'),
      isPopular: false,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.yearlyIds?.yearlyStarter?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.YEARLY,
      tier: PlanTier.STARTER,
    },
    {
      priceId: planPriceIds.yearly.yearlyPro,
      name: t('plans.pro.title'),
      features: t('plans.pro.features').split(';'),
      isPopular: true,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.yearlyIds?.yearlyPro?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.YEARLY,
      tier: PlanTier.PRO,
    },
    {
      priceId: planPriceIds.yearly.yearlyVoice,
      name: t('plans.proPlusVoice.title'),
      features: t('plans.proPlusVoice.features').split(';'),
      isPopular: false,
      ...(hasCurrentPriceId ? { isCurrent: planPriceIds?.yearlyIds?.yearlyVoice?.includes(currentPriceId) } : {}),
      billingMode: BILLING_MODES.YEARLY,
      tier: PlanTier.PRO_PLUS_VOICE,
    },
  ]
  return {
    monthly: monthlyPlans,
    yearly: yearlyPlans,
  }
}

export function convertNonNormalWhitespaceToWhitespace(str: string) {
  return str.replace(/[^\S ]+/g, ' ')
}

export function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Formats a numeric amount into a localized currency string.
// Example: formatCurrency(1234.5, 'USD', 'en') => "$1,234.50"
export function formatCurrency(amount: number, currency: string, locale: AvailableLocale = 'en'): string {
  if (amount === null || amount === undefined || Number.isNaN(amount as number)) return ''
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    // Fallback: naive formatting when currency/locale is unexpected
    const safe = typeof amount === 'number' ? amount.toFixed(2) : String(amount)
    return `${safe} ${currency || ''}`.trim()
  }
}

/**
 * Formats the amount and currency code for Paddle subscription details.
 * Paddle prioritizes using the smallest currency unit (cents for USD) to ensure calculation accuracy.
 * Converts USD amounts from cents to dollars.
 */
export function formatPaddlePriceByCurrencyCode(amount: number, currencyCode: string) {
  let newValue = amount

  if (currencyCode === 'USD') {
    // Paddle uses cents for USD
    newValue = Number((newValue / 100).toFixed(2))
  }

  return formatCurrency(newValue, currencyCode)
}

export function getClientUserAgent() {
  return window?.navigator?.userAgent
}

export function isIos(userAgent?: string) {
  return (
    /iphone|ipad|ipod/i.test(userAgent || getClientUserAgent()) ||
    ['iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  )
}

export function isPWA() {
  return window
    ? window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        ('standalone' in window.navigator && window.navigator.standalone)
    : undefined
}

export function getClientWebPushNotificationPlatform(): WebPushNotificationSubscriptionPlatform {
  const userAgent = getClientUserAgent()
  const isAndroid = /android/i.test(userAgent)
  const _isIOS = isIos(userAgent)
  const isWeb = /macintosh|windows|linux/i.test(userAgent)

  if (isPWA()) {
    if (isAndroid) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.PWA_ANDROID
    } else if (_isIOS) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.PWA_IOS
    } else if (isWeb) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.PWA_WEB
    }
  } else {
    if (isAndroid) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.ANDROID
    } else if (_isIOS) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.IOS
    } else if (isWeb) {
      return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.WEB
    }
  }

  return WEB_PUSH_NOTIFICATION_SUBSCRIPTION_PLATFORM.UNKNOWN
}

export function parseUserAgent(userAgent: string) {
  const ua = userAgent || ''
  let browser = ''
  let os = ''
  let device: (typeof USER_AGENT_DEVICE_TYPE)[keyof typeof USER_AGENT_DEVICE_TYPE] = USER_AGENT_DEVICE_TYPE.DESKTOP

  // detect browser (order matters due to overlapping substrings)
  if (/Edg\//.test(ua)) browser = 'Edge'
  else if (/OPR\//.test(ua)) browser = 'Opera'
  else if (/Firefox\//.test(ua)) browser = 'Firefox'
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari'
  else if (/Chrome\//.test(ua)) browser = 'Chrome'

  // get browser main version
  const versionMatch = ua.match(/(Chrome|Firefox|Safari|Edg|OPR)\/([\d.]+)/)
  const version = versionMatch && versionMatch[2] ? (versionMatch[2].split('.')[0]?.toString() ?? '') : ''

  // detect os
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11'
  else if (/Mac OS X/.test(ua) && /Mobile/.test(ua))
    os = 'iPadOS' // iPad pretending to be Mac
  else if (/Mac OS X/.test(ua)) os = 'macOS'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS'
  else if (/Linux/.test(ua)) os = 'Linux'

  // detect device type
  if (/Mobi|Android|iPhone|iPad|iPod/.test(ua)) device = USER_AGENT_DEVICE_TYPE.MOBILE

  return {
    browser,
    version,
    os,
    device,
  }
}

export const removeQueryParam = (paramName: string) => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(paramName)) return

  url.searchParams.delete(paramName)

  const newUrl = url.searchParams.toString() ? `${url.pathname}?${url.searchParams.toString()}` : url.pathname

  window.history.replaceState(null, '', newUrl)
}

export function getUserRoleLocalizationKey(role: UserRole) {
  switch (role) {
    case UserRole.OWNER:
      return 'User_Roles.owner'
    case UserRole.ADMIN:
      return 'User_Roles.admin'
    case UserRole.VIEWER:
      return 'User_Roles.viewer'
    default:
      return role
  }
}

// localStorage can throw on access (blocked site data, private modes, third-party iframes such as
// the embedded chat panel), so every helper below treats storage as best effort: a failed read is
// "nothing stored" and a failed write is skipped, instead of an exception that breaks the caller.
export const setLocalStorageWithExpiry = (key: string, value: string, ttlMs: number) => {
  if (typeof window === 'undefined') return
  const now = new Date()
  const item = {
    value,
    expiry: now.getTime() + ttlMs,
  }
  try {
    localStorage.setItem(key, JSON.stringify(item))
  } catch {
    // Not persisted; the value still applies for this page view.
  }
}

export const getLocalStorageWithExpiry = (key: string): string | null => {
  if (typeof window === 'undefined') return null

  try {
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null

    const item = JSON.parse(itemStr) as { value: string; expiry: number }
    const now = new Date()
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key)
      return null
    }
    return item.value
  } catch {
    return null
  }
}

export const removeLocalStorageItem = (key: string): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(key)
  } catch {
    // Nothing to remove from storage that cannot be reached.
  }
}

export const roundToInteger = (value?: number) => {
  if (value == null || !Number.isFinite(value)) return 0
  return Math.round(value)
}
